/** Shapes returned by the api/DocumentProcessing/ai/* endpoints. Mirrors DAS-Models/DocumentsModels. */

export type AiExtractionSource =
  | 'extraction'
  | 'category'
  | 'tag'
  | 'vision'
  | 'signature';

export interface DocumentAiSummary {
  documentProcessingId: number;
  documentName: string | null;
  briefEn: string | null;
  briefAr: string | null;
  aiCategory: string | null;
  aiCategoryIsNew: boolean | null;
  aiCategoryLookupId: number | null;
  aiMarkdownPath: string | null;
  mediaType: string | null;
  documentLanguage: string | null;
  hasTables: boolean | null;
  hasImages: boolean | null;
  tableCount: number | null;
  imageCount: number | null;
  chunksIndexed: number | null;
  isSignatureMissing: boolean | null;
  aiProcessedOn: string | null;
  isDocumentProcessed: boolean | null;
  isDocumentFailed: boolean | null;
}

export interface DocumentSegment {
  documentSegmentId: number;
  documentProcessingId: number;
  segmentIndex: number;
  pageNumber: number | null;
  text: string | null;
  summaryEn: string | null;
  summaryAr: string | null;
}

export interface DocumentExtraction {
  documentExtractionId: number;
  documentProcessingId: number;
  extractionKey: string;
  extractionValue: string | null;
  source: AiExtractionSource;
  /** The model wanted a term that is not in the DMA vocabulary yet. */
  isNewTerm: boolean;
  resolvedLookupId: number | null;
  resolvedTagId: number | null;
  /** null = awaiting review, true = accepted, false = rejected. */
  isAccepted: boolean | null;
  reviewedBy: number | null;
  reviewedOn: string | null;
}

export interface DocumentAiResult {
  summary: DocumentAiSummary;
  segments: DocumentSegment[];
  extractions: DocumentExtraction[];
  pendingSuggestions: DocumentExtraction[];
  /** Folder / department / category the document came from. */
  sourceContext: DocumentAiSourceContext | null;
  /** AI classification next to the value the user picked. */
  classification: DocumentAiClassificationComparison | null;
  /** Tags split into manual / AI-applied / AI-pending. */
  tags: DocumentAiTagComparison | null;
}

export type AiProcessingStage = 'queued' | 'processing' | 'completed' | 'failed';

/** Cheap poll shape - no text payload, safe to call every few seconds. */
export interface DocumentAiStatus {
  documentProcessingId: number;
  documentName: string | null;
  documentType: number | null;
  isDocumentProcessed: boolean | null;
  isDocumentFailed: boolean | null;
  comments: string | null;
  commentsAr: string | null;
  aiProcessedOn: string | null;
  aiCategory: string | null;
  mediaType: string | null;
  chunksIndexed: number | null;
  system_CreatedOn: string | null;
  segmentCount: number;
  extractionCount: number;
  pendingSuggestionCount: number;
  stage: AiProcessingStage;
  elapsedSeconds: number;
}

export interface DocumentAiOcr {
  documentProcessingId: number;
  markdown: string | null;
  path: string | null;
  segmentCount: number;
}

export interface DocumentAiSourceContext {
  documentProcessingId: number;
  sourceFileId: number | null;
  sourceDocumentId: number | null;
  sourceFolderId: number | null;
  sourceDepartmentId: number | null;
  sourceCategoryId: number | null;
  folderNameEn: string | null;
  folderNameAr: string | null;
  departmentNameEn: string | null;
  departmentNameAr: string | null;
  categoryNameEn: string | null;
  categoryNameAr: string | null;
}

export interface DocumentAiClassificationComparison {
  documentProcessingId: number;
  aiValue: string | null;
  aiLookupId: number | null;
  aiIsNew: boolean | null;
  aiResolvedNameEn: string | null;
  aiResolvedNameAr: string | null;
  documentId: number | null;
  manualLookupId: number | null;
  manualNameEn: string | null;
  manualNameAr: string | null;
  manualConfidentialityId: number | null;
  manualConfidentialityEn: string | null;
  matches: boolean;
}

export interface DocumentAiTagRow {
  tagMappingId: number | null;
  tagId: number | null;
  tagName: string;
  origin: 'manual' | 'ai';
  documentExtractionId: number | null;
  isApplied: boolean;
  isPending: boolean;
}

export interface DocumentAiTagComparison {
  documentProcessingId: number;
  fileId: number | null;
  documentId: number | null;
  manual: DocumentAiTagRow[];
  aiApplied: DocumentAiTagRow[];
  aiPending: DocumentAiTagRow[];
}

/** One row of the batched per-file lookup for list pages. */
export interface FileAiJob {
  fileId: number;
  documentProcessingId: number;
  isDocumentProcessed: boolean | null;
  isDocumentFailed: boolean | null;
  aiCategory: string | null;
  stage: AiProcessingStage;
}

export interface ApplyAiSuggestionItem {
  documentExtractionId: number;
  accept: boolean;
}

export interface ApplyAiSuggestionsRequest {
  documentProcessingId: number;
  items: ApplyAiSuggestionItem[];
  /** Creating brand-new tags is opt-in; default keeps new terms admin-only. */
  createMissingTags?: boolean;
}

export interface DocumentAiChatRequest {
  question: string;
  documentProcessingId?: number | null;
  folderId?: string | null;
  topK?: number;
}

export interface DocumentAiChatSource {
  chunkId?: string;
  documentId?: string;
  folderId?: string;
  pageNumber?: number;
  text?: string;
  score?: number;
  fileName?: string;
  language?: string;
}

export interface DocumentAiChatResponse {
  question: string;
  answer: string;
  sources: DocumentAiChatSource[];
}

export interface DocumentAiQueryRequest {
  query: string;
  documentProcessingId?: number | null;
  folderId?: string | null;
  topK?: number;
}

export interface DocumentAiQueryResponse {
  query: string;
  answer?: string;
  results: DocumentAiChatSource[];
}
